import { useEffect } from 'react';
import { useScrollPercentage } from 'react-scroll-percentage/dist';
import { Tween } from 'react-gsap';
import ReactGA from 'react-ga4';
import classnames from 'classnames';
import Header from './components/header';
import ProgressBar from './components/progress-bar';
import Image from './components/image';
import SectionIntro from './components/section/intro';
import SectionVideo from './components/section/video';
import SectionImage from './components/section/image';
import SectionImages from './components/section/images';
import SectionImageText from './components/section/image-text';
import SectionText from './components/section/text';
import SectionQuote from './components/section/quote';
import SectionSpotify from './components/section/spotify';
import Footer from './components/footer';
import styles from './App.module.scss';

ReactGA.initialize('G-1NHCZXL2CB');

function App() {
  let zIndex = 999;

  useEffect(() => {
    ReactGA.send('pageview');
  });

  const [ref, percentage] = useScrollPercentage({
    threshold: 0,
  });

  return (
    <div ref={ref} className={styles.App}>
      <Header />
      <ProgressBar progress={percentage} />

      <SectionVideo
        zIndex={zIndex--}
        src="leah-dou/Jewellery.mp4"
      />

      {/* Intro 01 */}
      <SectionIntro
        zIndex={zIndex--}
      />

      <SectionImage
        zIndex={zIndex--}
        scrub
        src="leah-dou/Cover TC.jpg"
        className={styles.sectionCover}
        credit="Summer 2023 Digital Cover featuring LEAH DOU"
        creditClassName={styles.credit}
      />

      {/* Intro 02 */}
      <SectionText
        zIndex={zIndex--}
        showTitlesOnSide
        className={styles.sectionTextOnly}
      >
        <Tween from={{ opacity: 0 }} to={{ opacity: 1 }}>
          <p className={styles.text}>
            音樂是一個神奇的媒介，它有著無窮的力量，能夠喚醒我們內心深處的回憶和情感。當我們聆聽著那些熟悉的旋律和歌詞時，過去的片段仿佛在眼前浮現，帶著我們穿越時光，回到那些特別的時刻。
            <br /><br />
            在新一期 COMPLEX 封面故事中，我們將與竇靖童一同踏上一段關於音樂與回憶的深刻旅程。透過她的歌單，不難發現每一首歌曲都承載著她的記憶碎片，我們將一窺她的回憶，聆聽那些陪伴她成長的旋律。這些歌曲如同時光隧道，將我們帶回那些特別的時刻：青春的起點、純真的瞬間、充滿喜悅或痛苦的日子。我們將跟隨著竇靖童的腳步，細細品味每一首歌曲背後的故事和情感。從她的歌單中，我們將聆聽到她的成長歷程、夢想追尋、愛與痛以及激動人心的轉折時刻。
            <br /><br />
            這場音樂之旅將帶我們重溫竇靖童的回憶，同時透過竇靖童的故事，重新發現那些令我們感動、懷念和熱愛的音樂。讓音樂的魔力將我們帶回過去，共同感受那些音符所承載的情感，在音樂的懷抱中重新連結我們的青春和回憶。
          </p>
        </Tween>
      </SectionText>

      <SectionImage
        zIndex={zIndex--}
        scrub
        src="leah-dou/0516_test0105 1 copyV4.jpg"
      />

      {/* Content 01 */}
      <SectionImageText
        zIndex={zIndex--}
        className={styles.section0254}
        imageClassName={styles.image}
        imageSrc="leah-dou/0516_test0254 copyV4.jpg"
        imagePosition="left"
        imageCredit={
          <>
            Jacket by COMMUN'S; Top by COMMUN'S; Pants by EYTYS From ENG; Shoes by untitlab<sup>®</sup>; Necklace by Chanel Vintage; Necklace by Le Fame; Ring by ABOAB; Scarf by Nan Knit.
          </>
        }
        imageCreditClassName={styles.credit}
        textClassName={styles.text}
        text={
          <>
            <Image src="leah-dou/text/title01.svg" className={styles.title} />
            <br />
            「回憶對我來說常常與味覺和聽覺聯繫在一起，而音樂在其中扮演著重要的角色，這些陪伴我成長的歌曲總是喚起許多美好回憶，讓我感受到那段時光的溫暖和純真。」在深入探討這個主題之前，竇靖童分享了她對音樂與回憶的獨特見解。
            <br /><br />
            竇靖童成長於音樂世家，音樂在她的生活中不僅僅是一種興趣和愛好，更是一直陪伴著她成長的夥伴。無論身處何地，音樂總像一位朋友、一位老師，引領她探索多樣的音樂風格和創作。「我記得自小只聽特定類型的音樂，但隨著成長，我開始拓寬音樂視野，接受各種不同類型的音樂。這種開放的態度也影響了我與他人的相處方式，以及對生活中各種事情的態度。」
            <br /><br />
            她繼續說道：「隨著時間的推移，我逐漸開始喜歡不同類型的音樂，從流行、搖滾到嘻哈和古典音樂，每個類型都帶給我不同的情感體驗。這些多樣的音樂風格使我在創作過程中更加開放和包容，也豐富了我的創作靈感。」
            <br /><br />
            在竇靖童的故事中，音樂扮演著一個奇妙的角色，不僅是她回憶的一部分，更塑造了她的成長和創作。
          </>
        }
      />

      <SectionVideo
        zIndex={zIndex--}
        src="leah-dou/Checked Coat.mp4"
      />

      {/* Quote 03 */}
      <SectionQuote
        zIndex={zIndex--}
        src="leah-dou/text/quote03.svg"
      />

      {/* Content 02 */}
      <SectionText
        zIndex={zIndex--}
        showTitlesOnSide
        className={styles.sectionTextOnly}
      >
        <Tween from={{ opacity: 0 }} to={{ opacity: 1 }}>
          <p className={styles.text}>
            <Image src="leah-dou/text/title02.svg" className={styles.title} />
            <br /><br />
            我們從竇靖童的童年時光開始談起，雖然她對於童年的音樂印象並不多，但記得家人常告訴她，當電視上播放廣告時伴隨著音樂的時候，她總會跟著廣告的音樂開心地跳起舞來。然而，當問及她第一次唱歌的經驗時，竇靖童記得跟著家人一起唱兒歌的時刻，那些兒歌成為了她的回憶，像是《採蘑菇的小姑娘》等等。這些兒歌都是她奶奶常常唱給她聽的，那些旋律讓她銘記於心。
            <br /><br />
            每當在不經意間聽到這些歌曲時，竇靖童馬上會聯想起與奶奶在一起的時光。有時候當她遇到其他人也曾聽過這些歌時，她會感到很神奇，因為這首歌對她來說只停留在與奶奶溫馨的回憶之中，她甚至從未聽過原版的歌曲。
            <iframe
              title="Spotify"
              src="https://open.spotify.com/embed/track/41G3ouRWqZUUjF3pGWBsEW"
              allowFullScreen
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              className={styles.spotify}
            />
            竇靖童與奶奶之間的特別回憶充滿了日常生活中的瑣碎細節，例如奶奶為她做飯、帶她去公園玩耍，或者在睡前給她講故事，每次回想都讓她感到非常溫暖。現在這些兒時的片段已經變得很零碎，但音樂卻有著將這些片段連結起來的力量。
            <br /><br />
            「這些兒時的回憶常常與音樂緊密相連，當我聽到這些歌曲時，整個畫面就像在我腦海中重現。音樂成為了一種觸發器，讓我能夠重新體驗那段溫馨而美好的時光。這樣的連結也讓我更加珍惜音樂的力量，它不僅僅是聽覺的享受，更是情感和回憶的寄託。」
            <br /><br />
            記憶力一直都不是竇靖童的強項，每當被問及前幾天做了些什麼時，她總是摸不著頭腦。然而，當她聽到音樂時，即使不能記得那時的具體情景，但也能馬上能夠感受到當時的情感和狀態，當聽著熟悉的旋律和歌詞時，過去的片段仿佛在眼前浮現，帶著她穿越時光，回到那些特別的時刻，回想起那種感受或當時的某種狀態。她相信音樂具有喚起過去的力量，讓回憶重現，這種感覺令她深感有趣而神奇。
            <br /><br />
            竇靖童的兒時音樂回憶不僅僅是個人寶貴的經驗，也對她的音樂之路產生了深遠的影響。這些回憶激發了她對音樂的熱愛和創作靈感，讓她能夠通過音樂表達自己並與人分享那份溫暖和純真。
          </p>
        </Tween>
      </SectionText>

      <SectionImages
        zIndex={zIndex--}
        className={styles.section96_91}
        leftSrc="leah-dou/0516_test0096 1 copyV4.jpg"
        leftClassName={styles.left}
        rightSrc="leah-dou/0516_test0091 1 copyV4.jpg"
        rightClassName={styles.right}
        rightCredit={
          <>
            Jacket by COMMUN'S; Top by AMIRI; Shoes by untitlab<sup>®</sup>; Necklace by ABOAB.
          </>
        }
        rightCreditClassName={styles.credit}
      />

      {/* Content 03 */}
      <SectionText
        zIndex={zIndex--}
        showTitlesOnSide
        className={styles.sectionTextOnly}
      >
        <Tween from={{ opacity: 0 }} to={{ opacity: 1 }}>
          <p className={styles.text}>
            <Image src="leah-dou/text/title03.svg" className={styles.title} />
            <br /><br />
            回想起中學時期，竇靖童提到同學們都開始接觸不同的音樂，她最常聽的是流行音樂，更偏愛歐美流行音樂以及樂隊。此外，竇靖童另一愛好是看動漫，她從而接觸到了日本歌曲，尤其是動漫的主題曲和片尾曲，擴大了她對音樂的好奇心。
            <br /><br />
            竇靖童特別提到她開始彈奏吉他的時候，看著別人彈奏著美妙的旋律，漸漸對吉他產生了濃厚的興趣，內心充滿了學習吉他的渴望。於是她買了一把吉他，開始探索音樂的世界。從最初只是在家隨意彈彈吉他，直到後來她創作自己第一首原創歌曲《With You》，並且第一次在 YouTube 上展示她的音樂才華和天賦。這段回憶仍然深深地刻在她的心中。
            <br /><br />
            這段青春時期的音樂和回憶，不僅是竇靖童音樂上的啟蒙，也塑造了她獨特的音樂風格。從流行音樂到歐美音樂，從華語歌曲到日本動漫歌曲，這些不同類型的音樂豐富了她的音樂體驗，並啟發了她的創作靈感。通過學習吉他，她更深入地了解了音樂的魅力，並開始了自己的創作之旅。這段青春歲月對竇靖童來說，不僅是音樂的成長歷程，也是她個人成長的重要時期。
            <br /><br />
          </p>
        </Tween>
      </SectionText>

      <SectionImageText
        zIndex={zIndex--}
        className={styles.sectionLyrics}
        imageClassName={styles.image}
        imageSrc="leah-dou/album-art/00.jpg"
        imagePosition="left"
        imageCredit="《With You》 Leah Dou"
        textClassName={styles.text}
        text={
          <>
            There isn't a minute I would spend without you<br />
            There isn't a moment I can breathe without you<br />
            Every piece of my heart belongs to you<br />
            Nothing can stop us now<br />
            I walked in with a smile on my face<br />
            Cause seeing you makes me feel living grace<br />
            So I need to tell you right right now something<br />
            I know you're scared of what the future holds<br />
            Don't get me wrong cause I do too<br />
            But right now I can tell you<br />
            You don't have to fear<br />
            Cause you and I are like the leaves and flower<br />
            Without you I can't live for an hour<br />
            I say you are crazy but I know that it is true<br />
            That if there was a forever I would spend with you<br />
            With you<br />
            You say you're down what's on your mind<br />
            I say nothing but I am hypnotized<br />
            But would this moment last forever forever<br />
            I know you're scare of what the future holds<br />
            Don't get me wrong cause I do too<br />
            But right now I can tell you<br />
            You don't have to fear<br />
            Cause you and I are like the leaves and flower<br />
            Without you I can't live for an hour<br />
            I say you are crazy but I know that it is true<br />
            That if there was a forever I would spend with you<br />
            With you
          </>
        }
      />

      <SectionVideo
        zIndex={zIndex--}
        src="leah-dou/Telephone.mp4"
      />

      {/* Content 04 */}
      <SectionImageText
        zIndex={zIndex--}
        className={styles.section0251}
        imageClassName={styles.image}
        imageSrc="leah-dou/0516_test0251 copyV4.jpg"
        imagePosition="right"
        textClassName={styles.text}
        text={
          <>
            當竇靖童在青春期燃點起音樂的熱愛後，當時 15 歲時的她決定要報考一所美國的表演藝術高中，因為她已經確定自己想要在音樂這個領域追求自己的夢想。
            <br /><br />
            她細心研究了該高中的報考條件，這所表演藝術高中提供了不同的音樂專業方向。他們有樂器、古典音樂或爵士音樂等不同的專業選項，還包括舞蹈和戲劇。當時，她思考著自己是要選擇偏向美聲和古典唱的聲樂課程，還是最新開設的創作歌手課程，專注於創作和演唱原創歌曲。最後她選擇了後者，但當時她從未寫過歌曲。然而，該學校的申請要求是提交兩首或三首原創歌曲，這讓她開始思考是否應該嘗試寫歌。
            <br /><br />
            這個經歷讓竇靖童開始思考自己想成為怎樣的歌手，當時這個問題對她來說並不是很清晰，然而，她帶著對音樂的熱愛和對唱歌的渴望，踏出了這重要的一步。
            <br /><br />
            當問到竇靖童，如果有時光機能回到自己在創作第一首歌的時候，她會給當時的自己有什麼提點或建議。竇靖童輕鬆地回答道：「我也不知道會給自己什麼意見（笑）。我覺得其實沒意見也挺好的，因為總得有一個開始。」 她並不後悔當初的選擇，因為她相信每個決定都有得有失。
          </>
        }
      />

      {/* Quote 04 */}
      <SectionQuote
        zIndex={zIndex--}
        src="leah-dou/text/quote04.svg"
      />

      <SectionText
        zIndex={zIndex--}
        className={styles.sectionTextWith3Images}
      >
        <>
          {/* Album Art 01-03 */}
          <div className={styles.images}>
            <Tween from={{ opacity: 0 }} to={{ opacity: 1 }}>
              <div>
                <Image src="leah-dou/album-art/01.jpg" />
                <div className={styles.albumName}>
                  《Hero's Come Back!!》nobodyknows+
                </div>
              </div>
            </Tween>
            <Tween from={{ opacity: 0 }} to={{ opacity: 1 }}>
              <div>
                <Image src="leah-dou/album-art/02.jpg" />
                <div className={styles.albumName}>
                  《Far Away》Nickelback
                </div>
              </div>
            </Tween>
            <Tween from={{ opacity: 0 }} to={{ opacity: 1 }}>
              <div>
                <Image src="leah-dou/album-art/03.jpg" />
                <div className={styles.albumName}>
                  《Apologize ft. One Republic》Timbaland
                </div>
              </div>
            </Tween>
          </div>
          {/* Content 05 */}
          <Tween from={{ opacity: 0 }} to={{ opacity: 1 }}>
            <p className={styles.text}>
              <Image src="leah-dou/text/title04.svg" className={styles.title} />
              <br /><br />
              談到竇靖童青春時期最深刻的歌曲，第一首就是《Hero's Come Back!!》。她是不折不扣的動漫粉絲，喜歡各種類型的動漫，包括充滿熱血、友情、勝利元素的，甚至帶暗黑元素的。透過這些動漫，她接觸到了許多以前未聽過的音樂，甚至認為如果能夠為動漫創作配樂將會是一件非常了不起的事情。
              <br /><br />
              除了動漫音樂，她也是個搖滾樂迷，喜歡那些節奏強烈、情感豐富的歌曲，像 Nickelback 和 One Republic 也是她當時喜愛的樂隊。她回憶起和朋友們一起上網聊天寫作業時，大家一邊播放音樂，有時她也會播放自己喜歡的歌曲，然後驚訝地發現大家都在聽同一首歌。「這些音樂能夠帶我們回到過去，懷念起當時的情感和簡單的生活。不論是在學校還是上網聊天，這些歌曲都伴隨著我們，成為青春時代的回憶。」
              <br /><br />
              「有時候，我會主動選擇一些歌曲來聆聽，然後陷入對過去的懷念之中，青少年時期的那份純真和簡單，那種無憂無慮的感覺，它們都深深地刻在我的心中。」在面對青少年時期的種種變化和挑戰，包括學業、愛情、未來、工作和音樂等，對她來說，音樂是一種無法言喻的情感表達方式，能夠在不同的階段和困境中找到一首歌來點亮她當時的內心。
              <br /><br />
              「與好友交談時，有時候言語無法完全表達我內心的感受，尤其是面對煩惱和困惑時，而音樂卻能夠以一種無法言喻的方式觸動我們的靈魂，更勝於千言萬語。當我與朋友們分享我所喜愛的歌曲時，那種被理解和安慰的感覺是無法用言語去表達，音樂讓我們更加靠近彼此。」
            </p>
          </Tween>
        </>
      </SectionText>

      <SectionImage
        zIndex={zIndex--}
        scrub
        src="leah-dou/0516_test0249 copyV4.jpg"
      />

      <SectionText
        zIndex={zIndex--}
        showTitlesOnSide
        className={styles.sectionTextWith3Images}
      >
        <>
          {/* Album Art 04-06 */}
          {/* Content 06 */}
          <Tween from={{ opacity: 0 }} to={{ opacity: 1 }}>
            <p className={styles.text}>
              <Image src="leah-dou/text/title05.svg" className={styles.title} />
              <br /><br />
              回憶到竇靖童年少時正式開始音樂創作的啟蒙期，我們可以發現她的每張專輯都有不同的風格，也沒有連貫的脈絡。創作過程中，她並不刻意尋找明確的定位，因為她喜歡嘗試不同的音樂元素，每張專輯都是全新的嘗試。她的音樂喜好和習慣經常變化，有時候她喜歡輕快的流行音樂，但並非每天都喜歡這樣的風格。她可能今天喜歡有節奏感的音樂，而明天可能想聽無人聲的曲子，後天則可能想聆聽不同音樂人的作品。她的聽歌習慣就像她的心情一樣多變。
              <br /><br />
              <Tween from={{ opacity: 0 }} to={{ opacity: 1 }}>
                <div className={styles.albumArtInText}>
                  <Image src="leah-dou/album-art/04.jpg" />
                  <div className={classnames(styles.albumName, styles.albumNameSingle)}>
                    《Stone Café》Leah Dou
                  </div>
                </div>
              </Tween>
              <br />
              回想起她早期的創作時光，每一張專輯都反映了不同狀態下的竇靖童。在 2016 年竇靖童推出了她的首張個人專輯《Stone Café》，這張專輯展現了她多元化的歌曲風格，融合了流行、搖滾、民謠和電子音樂等元素，彰顯了她的音樂實驗精神和創作才華。專輯深入探索了她青春期的情感、成長和尋找自我的主題，觸動聽眾的共鳴，其充滿概念性和獨特風格的作品讓樂迷帶來耳目一新的感覺，但對於竇靖童本人而言，《Stone Café》只是她那些時間碎片化作品的一個合輯，也是將她創作早期的毫無邊界和框架下的探索。
              <br /><br /><br />
              <Tween from={{ opacity: 0 }} to={{ opacity: 1 }}>
                <div className={styles.albumArtInText}>
                  <Image src="leah-dou/album-art/05.jpg" />
                  <div className={classnames(styles.albumName, styles.albumNameSingle)}>
                    《Kids Only》Leah Dou
                  </div>
                </div>
              </Tween>
              <br />
              她的第二張專輯《Kids Only》展現更加成熟的風格，這是一段她對待音樂創作特別嚴肅的時期，因為樂迷們對她的第二張專輯寄予了極高的期望，也有很多人會懷疑她能否像第一張專輯那樣成功。現在回想，她希望當時的自己能放鬆一點，不過也明白到自己必須歷那段緊張的時光才能明白自己需要放鬆。
              <br /><br /><br />
              <Tween from={{ opacity: 0 }} to={{ opacity: 1 }}>
                <div className={styles.albumArtInText}>
                  <Image src="leah-dou/album-art/06.jpg" />
                  <div className={classnames(styles.albumName, styles.albumNameSingle)}>
                    《GSG MIXTAPE》Leah Dou
                  </div>
                </div>
              </Tween>
              <br />
              竇靖童的下一張專輯《GSG MIXTAPE》，專輯名字是當時她在朋友家與一個小孩玩耍時被他稱為「Green Shy Guy」，她認為這張專輯也是她當時內心的一個寫照，混集了另類 R&B、New Wave 各種帶沉瀝和黑暗的元素。
              <br /><br />
              竇靖童進一步解釋道，她對待音樂就像對待她自己的言論和想法一樣，會隨時間變化。她可能在某個時刻表達了某個觀點或聲明，但一年後她的想法可能已經改變。音樂也是如此，她的喜好會隨著時間和經歷而改變。因此，她希望自己的創作能夠貼合當下的心境和想法，更自然地呈現出來。她相信這種方式更真實、更自然。這種特質讓樂迷們一直期待竇靖童在未來的創作中所展現的獨特音樂風格和豐富的創造力。
            </p>
          </Tween>
        </>
      </SectionText>

      <SectionImages
        zIndex={zIndex--}
        className={styles.section65_75}
        leftSrc="leah-dou/0516_test0265 1 copyV4.jpg"
        leftClassName={styles.left}
        rightSrc="leah-dou/0516_test0275 1 copyV4.jpg"
        rightClassName={styles.right}
        rightCredit={
          <>
            Jacket by MARNI; Pants by fabric qorn; Shoes by untitlab<sup>®</sup>; Necklace by YVMIN; Ring by Monica Vinader; Headphone by Marshall.
          </>
        }
        rightCreditClassName={styles.credit}
      />

      <SectionText
        zIndex={zIndex--}
        className={styles.sectionTextWith3Images}
      >
        <>
          {/* Album Art 07-09 */}
          <div className={styles.images}>
            <Tween from={{ opacity: 0 }} to={{ opacity: 1 }}>
              <div>
                <Image src="leah-dou/album-art/07.jpg" />
                <div className={styles.albumName}>
                  《Hollywood》The Black Skirts
                </div>
              </div>
            </Tween>
            <Tween from={{ opacity: 0 }} to={{ opacity: 1 }}>
              <div>
                <Image src="leah-dou/album-art/08.jpg" />
                <div className={styles.albumName}>
                  《K.》Cigarettes After Sex
                </div>
              </div>
            </Tween>
            <Tween from={{ opacity: 0 }} to={{ opacity: 1 }}>
              <div>
                <Image src="leah-dou/album-art/09.jpg" />
                <div className={styles.albumName}>
                  《Something About Us》Daft Punk
                </div>
              </div>
            </Tween>
          </div>

          {/* Content 07 */}
          <Tween from={{ opacity: 0 }} to={{ opacity: 1 }}>
            <p className={styles.text}>
              <Image src="leah-dou/text/title06.svg" className={styles.title} />
              <br /><br />
              二十出頭的竇靖童對於新音樂的渴望並沒有停止，繼續尋找不同類型的音樂。來自韓國的 The Black Skirts 讓她了解到有如此溫柔的電子音樂風格。他的歌曲《Hollywood》讓她想起了一個朋友以及當時她剛回到北京的情景。這位朋友帶她認識到不同的人，品嚐在地的美食，一起玩樂，還向她介紹了許多好聽的音樂。每次聽到《Hollywood》，竇靖童都會回憶起那段一起度過的美好時光。
              <br /><br />
              還有 Cigarettes After Sex 的《K.》也一直深深吸引著竇靖童，一直放在循環播放的歌單中。這首歌讓她感受到一種柔軟的特質，彷彿能從歌曲中感受到一個人的溫柔。
              <br /><br />
              此外，Daft Punk 的《Something About Us》在她心中也具有特殊的意義，能夠引發她強烈的情感回饋，甚至每當聽到這首歌時，總會喚起一種快要哭泣的感覺。令她感動的並不來自歌詞或演唱技巧，而是兩個冷漠的機械人在探討人際關係的方式。每次聽到這首歌，竇靖童就會想到各種人與人之間的情感，比如朋友、家人、愛人等等，喚起她一種難以言喻的感覺。
              <br /><br />
              這首歌激發了她對作曲和編曲的探索，讓她思考背後是什麼驅使著這首音樂能夠傳遞如此濃烈的情感。這進一步引發了她在創作上對音樂細節和情感表達的關注，同時也讓她對自己的音樂喜好有了更深層次的理解。
            </p>
          </Tween>
        </>
      </SectionText>

      <SectionVideo
        zIndex={zIndex--}
        src="leah-dou/Leather Jacket Look.mp4"
      />

      {/* Quote 05 */}
      <SectionQuote
        zIndex={zIndex--}
        src="leah-dou/text/quote05.svg"
      />

      <SectionText
        zIndex={zIndex--}
        showTitlesOnSide
        className={styles.sectionTextWith3Images}
      >
        <>
          {/* Album Art 10 */}
          <div className={classnames(styles.images, styles.centered)}>
            <Tween from={{ opacity: 0 }} to={{ opacity: 1 }}>
              <div>
                <Image src="leah-dou/album-art/10.jpg" />
                <div className={classnames(styles.albumName, styles.albumNameSingle)}>
                  《春遊》Leah Dou
                </div>
              </div>
            </Tween>
          </div>

          {/* Content 08 */}
          <Tween from={{ opacity: 0 }} to={{ opacity: 1 }}>
            <p className={styles.text}>
              <Image src="leah-dou/text/title07.svg" className={styles.title} />
              <br /><br />
              今年四月竇靖童正式發布了她的新專輯《春遊》，這不僅是她的第一張全中文專輯，同時也為樂迷們帶來了耳目一新的清新感覺。
              <br /><br />
              「在創作的過程中，我一開始並沒有明確知道這張專輯要表達什麼或者風格如何，是在創作的過程中這些主題和想法才會漸漸浮現出來，並慢慢將它們串聯起來，最後才決定給專輯取一個名字 –《春遊》。我希望這張專輯能夠傳達一種輕鬆、愉快的感覺，而不是沉重的負擔。」這種創作方式讓她能夠更加自由地表達自己，不受框架的限制。
              <br /><br />
              她進一步解釋道：「在過去的三年中，我逐漸累積了一個想法，就是為什麼音樂總是如此嚴肅？為什麼創作上總是陷入困惑和壓力中，難道是追求所謂的完美？在這張專輯中，雖然我沒有完全放棄這些思考，但我更接近了我想要的方向。我認為音樂並不一定要如此嚴肅，它可以是輕鬆的、愉快的，對於我的樂迷來說也可以說是一種解放，是不需要過份努力才能理解和享受的音樂。」
              <br /><br />
              最後，竇靖童用一個非常有趣的比喻形容這張新專輯：「如果上一張專輯是像紅酒那樣，需要用心去品味，那這次的專輯就像啤酒或清酒一樣，清爽輕快。我希望這首歌能給人一種鼓勵和愉悅的感覺，它是一種不需要太多思考就能享受的音樂。」《春遊》對於竇靖童來說也是這三年時間裡的種種經歷與回憶。
            </p>
          </Tween>
        </>
      </SectionText>

      <SectionImage
        zIndex={zIndex--}
        scrub
        src="leah-dou/0516_test0147 copyV4.jpg"
      />

      {/* Embed Playlist */}
      <SectionSpotify
        zIndex={zIndex--}
        src="https://open.spotify.com/embed/album/5jDPqG78AQo13DOTc6S5a2?utm_source=generator"
      />

      {/* Content 09 */}
      <SectionImageText
        zIndex={zIndex--}
        className={styles.section15}
        imageClassName={styles.image}
        imageSrc="leah-dou/Cover story Leah Dou FInal Edit RAW-15 copy V4.jpg"
        imagePosition="left"
        imageCredit={'Jacket by PRADA; Shirt by PRADA; Shorts by PRADA; Shoes by PRADA.'}
        imageCreditClassName={styles.credit}
        textClassName={styles.text}
        text={
          <>
            <Image src="leah-dou/text/lyric01.svg" className={styles.title} />
            <br />
            關於《狗熊》這首歌，竇靖童分享了一個有趣的回憶。兒時她喜歡特意背上很重的書包，沉重的書包給予她安全感。她習慣將一周的課本都背在身上，以免忘記任何一本課本。這種習慣源於她對安全感的渴望，認為這樣才能讓自己站穩腳步與這個世界接觸，《狗熊》是希望最後自己能放下內心的石頭，釋放心中的重擔。
            <br /><br />
            <Image src="leah-dou/text/lyric02.svg" className={styles.title} />
            <br />
            《河流》這首歌不僅與愛情有關，更是竇靖童對人際關係和人生旅程的感悟。她認為每個人都在獨自走一條自己的路，但偶爾會遇到與自己平行交疊的人，就像河流中的支流交匯一樣。這種交匯可能是一段浪漫的愛情，也可能是新友誼的建立、工作中不同領域的交集。竇靖童認為人際關係和事物的變遷就像河流般不斷分合。她將這種感悟融入了歌曲中，表達對人際關係和愛情的理解。
            <br /><br />
            <Image src="leah-dou/text/lyric03.svg" className={styles.title} />
            <br />
            談及過去幾年是否有一個特別的時刻或事件讓她覺得自己成長了？ 「或許有吧。以前我常常害怕與人產生衝突，不喜歡這樣的情況發生。而現在，我發現自己不再害怕這種情況了。或許不是說我不害怕，而是我明白有時候需要去面對、堅定地處理這事情，包括自己的想法，也不再迫切地需要得到他人的支持。有時候，有些事情，你必須堅定地去做，就像歌中的一句歌詞所說，『不再需要被人圍繞對我微笑』。對我而言就是成長的一種狀態，一種慢慢成長的感覺。」
          </>
        }
      />

      {/* Quote 06 */}
      <SectionQuote
        zIndex={zIndex--}
        src="leah-dou/text/quote06.svg"
      />

      {/* Content 10 */}
      <SectionText
        zIndex={zIndex--}
        showTitlesOnSide
        className={styles.sectionTextOnly}
      >
        <Tween from={{ opacity: 0 }} to={{ opacity: 1 }}>
          <p className={styles.text}>
            <Image src="leah-dou/text/title08.svg" className={styles.title} />
            <br /><br />
            竇靖童在新專輯中融合了個人的成長經歷和獨特的人生思考，無論是對安全感的追求還是對人際關係的感性覺悟，通過動人而且輕鬆的全新音樂作品與樂迷在成長與回憶中產生共鳴。
            <br /><br />
            最後，竇靖童也希望自己的音樂能夠成為樂迷們的回憶，成為一個承載著他們回憶的媒介。「我自己作為一個樂迷，當我聽到能喚起我回憶的歌曲時，我就會想，如果我的歌曲也能給他人帶來這樣的感覺，那就太開心了，是一件很棒的事情。」
            <br /><br />
            因為在音樂的世界裡，回憶得以永恆，甚至成為我們彼此之間的連結。
          </p>
        </Tween>
      </SectionText>

      <SectionVideo
        zIndex={zIndex--}
        src="leah-dou/Fisheye2.mp4"
        isLast
      />

      <Footer
        credits={[
          { title: 'EXECUTIVE PRODUCER', name: 'BONNIE CHAN WOO' },
          { title: 'INTERVIEWER', name: 'OSCAR LAW' },
          { title: 'WRITER', name: 'OSCAR LAW' },
          { title: 'EDITOR', name: 'ALEXIA ZHOU & MOHAN ZHANG' },
          { title: 'DESIGN DIRECTION', name: 'NICOLE LAW & JKING XU' },
          { title: 'PHOTOGRAPHER', name: 'BENSONVIEWS' },
          { title: 'LIGHTING TECHNICIAN', name: 'SEAN LEUNG' },
          { title: 'PHOTOGRAPHY ASSISTANT', name: 'MIN ZHANG & ZIXUAN WANG' },
          { title: 'PHOTO RETOUCHER', name: 'ALAN CHEUNG' },
          { title: 'TALENT MANAGEMENT', name: 'SERENE DU & MANNING ZHOU' },
          { title: 'VIDEO PRODUCTION DIRECTOR', name: 'AH P' },
          { title: 'ASSISTANT PRODUCER', name: 'PTCM' },
          { title: 'VIDEO EDITOR', name: 'DAWN TANG' },
          { title: 'STYLIST', name: 'CHERRYGUN 陳星如' },
          { title: 'STYLING ASSISTANT', name: 'NANNAN & ZX & ZE @ L0FI' },
          { title: 'MAKE UP', name: 'KENIELLE' },
          { title: 'HAIR', name: 'HAN BIN（SALSA STUDIO）' },
          { title: 'WEB DEVELOPER', name: 'MATT YIU' },
          { title: 'SPECIAL THANKS', name: 'MARSHALL' },
        ]}
      />
    </div>
  );
}

export default App;
